import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { ArrowLeft, ChevronDown, FileText, PlusCircle, Search, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DIFFICULTY, type Difficulty as DifficultyEnum } from "@/integrations/backend/enums";
import {
  confirmProgramCreation,
  generateProgramCreation,
  getProgramCreationChat,
  parseProgramCreationAssistantContent,
  restartProgramCreation,
  sendProgramCreationMessage,
  type AIGenerationChatMessage,
  type AIGenerationConversation,
  type ProgramCreationPreviewPayload,
} from "@/integrations/backend/program-generation";
import {
  assignProgramTemplateToClient,
  createProgramForClient,
  listPrograms,
  type ClientProgram,
  type CreateProgramInput,
  type Program,
} from "@/integrations/backend/programs";
import { motivationOptions, programExerciseTypeOptions } from "@/lib/onboarding-options";
import { buildChatErrorCopyOptions } from "@/lib/format-error-for-copy";
import ProgramGenerationChatModal from "./ProgramGenerationChatModal";
import ProgramEditorFields, { createDefaultProgramEditorValue } from "./program-editor-fields";
import { toastCopyableError } from "./copyable-error-toast";

interface AssignProgramDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  hasAssignedProgram: boolean;
  onAssigned: (clientProgram: ClientProgram) => void;
}

type Step = "choice" | "template" | "scratch";

export default function AssignProgramDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  hasAssignedProgram,
  onAssigned,
}: AssignProgramDialogProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>("choice");
  const programsQueryKey = queryKeys.programs.all;
  const programsQuery = useQuery({
    queryKey: programsQueryKey,
    queryFn: listPrograms,
    enabled: open && step === "template",
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...programsQueryKey]),
  });
  const availablePrograms = programsQuery.data ?? [];
  const programsLoading = programsQuery.isPending;

  const [selectedProgramTemplateId, setSelectedProgramTemplateId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMotivations, setFilterMotivations] = useState<string[]>([]);
  const [filterDifficulties, setFilterDifficulties] = useState<DifficultyEnum[]>([]);
  const [filterFrequencies, setFilterFrequencies] = useState<number[]>([]);
  const [filterExerciseTypes, setFilterExerciseTypes] = useState<string[]>([]);
  const [filterDurationMin, setFilterDurationMin] = useState<number | undefined>(undefined);
  const [filterDurationMax, setFilterDurationMax] = useState<number | undefined>(undefined);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scratchForm, setScratchForm] = useState<CreateProgramInput>(createDefaultProgramEditorValue);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiConversation, setAiConversation] = useState<AIGenerationConversation | null>(null);
  const [aiChatInput, setAiChatInput] = useState("");
  const [aiChatBusy, setAiChatBusy] = useState(false);

  useEffect(() => {
    if (programsQuery.isError && programsQuery.error) {
      toast.error("Failed to load programs", { description: getErrorMessage(programsQuery.error, "Failed to load programs") });
    }
  }, [programsQuery.isError, programsQuery.error]);

  useEffect(() => {
    if (!open) {
      setStep("choice");
      setSelectedProgramTemplateId(null);
      setSearchTerm("");
      setFilterMotivations([]);
      setFilterDifficulties([]);
      setFilterFrequencies([]);
      setFilterExerciseTypes([]);
      setFilterDurationMin(undefined);
      setFilterDurationMax(undefined);
      setFiltersOpen(false);
      setSubmitting(false);
      setScratchForm(createDefaultProgramEditorValue());
      setAiChatOpen(false);
      setAiConversation(null);
      setAiChatInput("");
      setAiChatBusy(false);
    }
  }, [open]);

  const getErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error && error.message) return error.message;
    return fallback;
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const assistantMessageCount = (conversation: AIGenerationConversation | null): number =>
    (conversation?.messages ?? []).filter((m) => m.role === "assistant").length;

  const waitForAssistantResponse = async ({
    conversationId,
    initialAssistantCount,
    fetchConversation,
    applyConversation,
  }: {
    conversationId: string | null;
    initialAssistantCount: number;
    fetchConversation: (conversationId: string) => Promise<AIGenerationConversation>;
    applyConversation: (conversation: AIGenerationConversation) => void;
  }): Promise<AIGenerationConversation> => {
    if (!conversationId) {
      throw new Error("conversation_id is required");
    }
    const timeoutMs = 600_000;
    const intervalMs = 2_000;
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      await sleep(intervalMs);
      const next = await fetchConversation(conversationId);
      applyConversation(next);
      if (assistantMessageCount(next) > initialAssistantCount) {
        return next;
      }
    }
    throw new Error("Timed out waiting for AI response");
  };

  const toggleFilter = (current: string[], value: string, setter: (next: string[]) => void) => {
    setter(current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value]);
  };

  const toggleDifficultyFilter = (difficulty: DifficultyEnum) => {
    setFilterDifficulties((prev) =>
      prev.includes(difficulty) ? prev.filter((entry) => entry !== difficulty) : [...prev, difficulty]
    );
  };

  const toggleFrequencyFilter = (frequency: number) => {
    setFilterFrequencies((prev) =>
      prev.includes(frequency) ? prev.filter((entry) => entry !== frequency) : [...prev, frequency]
    );
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterMotivations([]);
    setFilterDifficulties([]);
    setFilterFrequencies([]);
    setFilterExerciseTypes([]);
    setFilterDurationMin(undefined);
    setFilterDurationMax(undefined);
  };

  const filteredPrograms = useMemo(() => {
    return availablePrograms.filter((program) => {
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          program.name.toLowerCase().includes(searchLower) ||
          program.description.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (filterMotivations.length > 0) {
        const hasMatchingMotivation = filterMotivations.some((motivationId) =>
          program.motivations.includes(motivationId)
        );
        if (!hasMatchingMotivation) return false;
      }

      if (filterDifficulties.length > 0 && !filterDifficulties.includes(program.difficulty as DifficultyEnum)) {
        return false;
      }

      if (filterFrequencies.length > 0 && !filterFrequencies.includes(program.frequency)) {
        return false;
      }

      if (filterExerciseTypes.length > 0) {
        const hasMatchingExerciseType = filterExerciseTypes.some((exerciseTypeId) =>
          program.exercise_types.includes(exerciseTypeId)
        );
        if (!hasMatchingExerciseType) return false;
      }

      if (filterDurationMin !== undefined && program.duration < filterDurationMin) return false;
      if (filterDurationMax !== undefined && program.duration > filterDurationMax) return false;

      return true;
    });
  }, [
    availablePrograms,
    filterDifficulties,
    filterDurationMax,
    filterDurationMin,
    filterExerciseTypes,
    filterFrequencies,
    filterMotivations,
    searchTerm,
  ]);

  const activeFilterCount = useMemo(
    () =>
      (searchTerm ? 1 : 0) +
      (filterMotivations.length > 0 ? 1 : 0) +
      (filterDifficulties.length > 0 ? 1 : 0) +
      (filterFrequencies.length > 0 ? 1 : 0) +
      (filterExerciseTypes.length > 0 ? 1 : 0) +
      (filterDurationMin !== undefined || filterDurationMax !== undefined ? 1 : 0),
    [
      filterDifficulties.length,
      filterDurationMax,
      filterDurationMin,
      filterExerciseTypes.length,
      filterFrequencies.length,
      filterMotivations.length,
      searchTerm,
    ]
  );

  const invalidateProgramCaches = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.clientPrograms(clientId) });
    await queryClient.invalidateQueries({ queryKey: programsQueryKey });
  }, [clientId, queryClient, programsQueryKey]);

  const getDifficultyColor = (level: string) => {
    const colorMap: Record<string, string> = {
      Beginner: "bg-green-100 text-green-800",
      Intermediate: "bg-yellow-100 text-yellow-800",
      Advanced: "bg-blue-100 text-blue-800",
      Expert: "bg-orange-100 text-orange-800",
      Pro: "bg-red-100 text-red-800",
    };
    return colorMap[level] || "bg-gray-100 text-gray-800";
  };

  const handleUseTemplate = async () => {
    if (!selectedProgramTemplateId) return;
    setSubmitting(true);
    try {
      const clientProgram = await assignProgramTemplateToClient(clientId, selectedProgramTemplateId);
      onAssigned(clientProgram);
      await invalidateProgramCaches();
      toast.success(hasAssignedProgram ? "New program assigned successfully" : "Program assigned successfully");
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error("Could not assign program", { description: getErrorMessage(error, "Could not assign program") });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateFromScratch = async () => {
    if (
      !scratchForm.name.trim() ||
      !scratchForm.description.trim() ||
      scratchForm.duration <= 0 ||
      scratchForm.frequency <= 0
    ) {
      toast.error("Please fill in all required program fields.");
      return;
    }

    setSubmitting(true);
    try {
      const clientProgram = await createProgramForClient(clientId, {
        ...scratchForm,
        name: scratchForm.name.trim(),
        description: scratchForm.description.trim(),
        program_focus: scratchForm.program_focus?.trim() || "",
        what_to_expect: scratchForm.what_to_expect?.trim() || "",
        variety: scratchForm.variety?.trim() || "",
      });
      onAssigned(clientProgram);
      await invalidateProgramCaches();
      toast.success(hasAssignedProgram ? "New program assigned successfully" : "Program assigned successfully");
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error("Could not create program", { description: getErrorMessage(error, "Could not create program") });
    } finally {
      setSubmitting(false);
    }
  };

  const aiAssistantMessages = useMemo(
    () => (aiConversation?.messages ?? []).filter((m) => m.role === "assistant"),
    [aiConversation]
  );

  const latestProgramPreview = useMemo<ProgramCreationPreviewPayload | null>(() => {
    if (aiConversation?.parsed_preview && "name" in aiConversation.parsed_preview) {
      return aiConversation.parsed_preview as ProgramCreationPreviewPayload;
    }
    const lastAssistant = aiAssistantMessages[aiAssistantMessages.length - 1];
    return parseProgramCreationAssistantContent(lastAssistant?.content ?? "");
  }, [aiAssistantMessages, aiConversation]);

  const latestProgramParseError = useMemo(() => aiConversation?.parse_error ?? null, [aiConversation]);

  const handleGenerateProgramWithAI = async () => {
    setAiChatOpen(true);
    setAiChatInput("");
    setAiConversation(null);
    setAiChatBusy(true);
    let errorConversation = aiConversation;
    const applyAiConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setAiConversation(next);
    };
    try {
      const conversation = await generateProgramCreation(clientId);
      applyAiConversation(conversation);
      await waitForAssistantResponse({
        conversationId: conversation.conversation_id,
        initialAssistantCount: assistantMessageCount(conversation),
        fetchConversation: (conversationId) => getProgramCreationChat(clientId, conversationId),
        applyConversation: applyAiConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to generate program", buildChatErrorCopyOptions(error, errorConversation));
      setAiChatOpen(false);
    } finally {
      setAiChatBusy(false);
    }
  };

  const handleSendProgramCreationMessage = async () => {
    const content = aiChatInput.trim();
    const conversationId = aiConversation?.conversation_id;
    if (!content || !conversationId) return;
    const optimisticMessage: AIGenerationChatMessage = {
      id: `optimistic-${Date.now()}`,
      profile_id: clientId,
      chat_type: "program_creation",
      conversation_id: conversationId,
      role: "user",
      content,
      created_at: null,
      updated_at: null,
    };
    setAiConversation((prev) =>
      prev
        ? { ...prev, messages: [...prev.messages, optimisticMessage] }
        : prev
    );
    setAiChatInput("");
    setAiChatBusy(true);
    let errorConversation = aiConversation;
    const applyAiConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setAiConversation(next);
    };
    try {
      const next = await sendProgramCreationMessage(clientId, {
        conversation_id: conversationId,
        content,
      });
      applyAiConversation(next);
      await waitForAssistantResponse({
        conversationId: next.conversation_id,
        initialAssistantCount: assistantMessageCount(next),
        fetchConversation: (nextConversationId) => getProgramCreationChat(clientId, nextConversationId),
        applyConversation: applyAiConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to send message", buildChatErrorCopyOptions(error, errorConversation, content));
      setAiConversation((prev) =>
        prev
          ? { ...prev, messages: prev.messages.filter((m) => m.id !== optimisticMessage.id) }
          : prev
      );
    } finally {
      setAiChatBusy(false);
    }
  };

  const handleRestartProgramCreationConversation = async () => {
    setAiChatBusy(true);
    let errorConversation = aiConversation;
    const applyAiConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setAiConversation(next);
    };
    try {
      const next = await restartProgramCreation(clientId);
      applyAiConversation(next);
      setAiChatInput("");
      await waitForAssistantResponse({
        conversationId: next.conversation_id,
        initialAssistantCount: assistantMessageCount(next),
        fetchConversation: (conversationId) => getProgramCreationChat(clientId, conversationId),
        applyConversation: applyAiConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to restart conversation", buildChatErrorCopyOptions(error, errorConversation));
    } finally {
      setAiChatBusy(false);
    }
  };

  const handleConfirmProgramCreation = async () => {
    const conversationId = aiConversation?.conversation_id;
    if (!conversationId) return;
    setAiChatBusy(true);
    try {
      const clientProgram = await confirmProgramCreation(clientId, conversationId);
      onAssigned(clientProgram);
      await invalidateProgramCaches();
      setAiChatOpen(false);
      onOpenChange(false);
      toast.success(hasAssignedProgram ? "New program assigned successfully" : "Program assigned successfully");
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to confirm and assign program", buildChatErrorCopyOptions(error, aiConversation));
    } finally {
      setAiChatBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogScrollContent
          className={`w-[calc(100vw-2rem)] bg-white text-black ${
            step === "choice" ? "sm:max-w-[720px]" : "sm:max-w-[900px]"
          }`}
        >
          {step === "choice" ? (
            <>
              <DialogHeader className="shrink-0">
                <DialogTitle>{hasAssignedProgram ? "Assign New Program" : "Assign Program"}</DialogTitle>
                <DialogDescription>Choose how to create a program for {clientName || "this client"}.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <Card
                  className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-primary"
                  onClick={() => setStep("template")}
                >
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Create from Template</h3>
                    <p className="text-sm text-gray-600">
                      Start with a shared program template, then assign a client-specific copy.
                    </p>
                  </CardContent>
                </Card>
                <Card
                  className="cursor-pointer hover:shadow-lg transition-all border-2 border-dashed hover:border-primary"
                  onClick={() => void handleGenerateProgramWithAI()}
                >
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mb-4">
                      <Sparkles className="w-6 h-6 text-violet-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Create with AI</h3>
                    <p className="text-sm text-gray-600">
                      Generate a client-specific program using onboarding details, notes, and templates.
                    </p>
                  </CardContent>
                </Card>
                <Card
                  className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-primary"
                  onClick={() => setStep("scratch")}
                >
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                      <PlusCircle className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Create from Scratch</h3>
                    <p className="text-sm text-gray-600">
                      Build a fresh client-specific program from the ground up.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : step === "template" ? (
            <>
            <DialogHeader className="shrink-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <DialogTitle>Select Program Template</DialogTitle>
                  <DialogDescription>Search and filter to find the right starting point.</DialogDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setStep("choice")} className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              </div>
            </DialogHeader>

            <DialogBody>
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search programs by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="mb-6 space-y-4 p-4 border rounded-lg bg-gray-50">
                <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
                      {activeFilterCount > 0 && <span className="text-xs text-gray-600">({activeFilterCount} active)</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {activeFilterCount > 0 && (
                        <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs h-7">
                          <X className="w-3 h-3 mr-1" />
                          Clear All
                        </Button>
                      )}
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" size="sm" className="text-xs h-7">
                          {filtersOpen ? "Hide filters" : "Show filters"}
                          <ChevronDown className="w-3 h-3 ml-1" />
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                  </div>

                  <CollapsibleContent className="pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-gray-700">Motivations</Label>
                        <div className="flex flex-wrap gap-2">
                          {motivationOptions.map((motivation) => (
                            <div key={motivation.id} className="flex items-center space-x-1">
                              <Checkbox
                                id={`program-filter-motivation-${motivation.id}`}
                                checked={filterMotivations.includes(motivation.id)}
                                onCheckedChange={() =>
                                  toggleFilter(filterMotivations, motivation.id, setFilterMotivations)
                                }
                              />
                              <Label
                                htmlFor={`program-filter-motivation-${motivation.id}`}
                                className="text-xs font-normal cursor-pointer"
                              >
                                {motivation.icon}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-gray-700">Difficulty</Label>
                        <div className="flex flex-wrap gap-2">
                          {(DIFFICULTY as unknown as string[]).map((difficulty) => (
                            <div key={difficulty} className="flex items-center space-x-1">
                              <Checkbox
                                id={`program-filter-difficulty-${difficulty}`}
                                checked={filterDifficulties.includes(difficulty as DifficultyEnum)}
                                onCheckedChange={() => toggleDifficultyFilter(difficulty as DifficultyEnum)}
                              />
                              <Label
                                htmlFor={`program-filter-difficulty-${difficulty}`}
                                className="text-xs font-normal cursor-pointer"
                              >
                                {difficulty}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-gray-700">Frequency (sessions/week)</Label>
                        <div className="flex flex-wrap gap-2">
                          {[1, 2, 3, 4, 5, 6, 7].map((freq) => (
                            <div key={freq} className="flex items-center space-x-1">
                              <Checkbox
                                id={`program-filter-frequency-${freq}`}
                                checked={filterFrequencies.includes(freq)}
                                onCheckedChange={() => toggleFrequencyFilter(freq)}
                              />
                              <Label
                                htmlFor={`program-filter-frequency-${freq}`}
                                className="text-xs font-normal cursor-pointer"
                              >
                                {freq}x
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-gray-700">Exercise Types</Label>
                        <div className="flex flex-wrap gap-2">
                          {programExerciseTypeOptions.map((exerciseType) => (
                            <div key={exerciseType.id} className="flex items-center space-x-1">
                              <Checkbox
                                id={`program-filter-exercise-type-${exerciseType.id}`}
                                checked={filterExerciseTypes.includes(exerciseType.id)}
                                onCheckedChange={() =>
                                  toggleFilter(filterExerciseTypes, exerciseType.id, setFilterExerciseTypes)
                                }
                              />
                              <Label
                                htmlFor={`program-filter-exercise-type-${exerciseType.id}`}
                                className="text-xs font-normal cursor-pointer"
                              >
                                {exerciseType.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-gray-700">Duration (weeks)</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label htmlFor="program-duration-min" className="text-xs text-gray-600">
                              Min
                            </Label>
                            <Input
                              id="program-duration-min"
                              type="number"
                              min="1"
                              placeholder="Min"
                              value={filterDurationMin || ""}
                              onChange={(e) =>
                                setFilterDurationMin(e.target.value ? parseInt(e.target.value, 10) : undefined)
                              }
                              className="h-8 text-xs"
                            />
                          </div>
                          <div>
                            <Label htmlFor="program-duration-max" className="text-xs text-gray-600">
                              Max
                            </Label>
                            <Input
                              id="program-duration-max"
                              type="number"
                              min="1"
                              placeholder="Max"
                              value={filterDurationMax || ""}
                              onChange={(e) =>
                                setFilterDurationMax(e.target.value ? parseInt(e.target.value, 10) : undefined)
                              }
                              className="h-8 text-xs"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>

              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {filteredPrograms.length} of {availablePrograms.length} programs
                </p>
              </div>

              {programsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading programs...</div>
              ) : (
                <div className="space-y-3">
                  {filteredPrograms.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No programs match your filters.</p>
                      <Button variant="link" onClick={clearAllFilters} className="mt-2">
                        Clear filters
                      </Button>
                    </div>
                  ) : (
                    filteredPrograms.map((program) => (
                      <Card
                        key={program.id}
                        className={`cursor-pointer border-2 transition-all ${
                          selectedProgramTemplateId === program.id
                            ? "border-primary shadow-md"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        onClick={() => setSelectedProgramTemplateId(program.id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 mb-1">{program.name}</h4>
                              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{program.description}</p>
                              <div className="flex flex-wrap gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {program.duration} weeks
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {program.frequency}x/week
                                </Badge>
                                {program.weekly_schedule_preference?.trim() ? (
                                  <Badge variant="outline" className="text-xs">
                                    Preferred schedule set
                                  </Badge>
                                ) : null}
                                <Badge className={`${getDifficultyColor(program.difficulty)} text-xs`}>
                                  {program.difficulty}
                                </Badge>
                                {program.motivations.length > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {program.motivations.length} motivation{program.motivations.length === 1 ? "" : "s"}
                                  </Badge>
                                )}
                                {program.exercise_types.length > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    {program.exercise_types.length} exercise type
                                    {program.exercise_types.length === 1 ? "" : "s"}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Checkbox
                              checked={selectedProgramTemplateId === program.id}
                              onCheckedChange={() => setSelectedProgramTemplateId(program.id)}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              )}
            </DialogBody>

            <DialogFooter className="shrink-0 border-t pt-4 mt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUseTemplate}
                disabled={!selectedProgramTemplateId || submitting}
                className="bg-gradient-to-r from-primary to-lime-hover"
              >
                {submitting ? "Assigning..." : hasAssignedProgram ? "Assign New Program" : "Assign Program"}
              </Button>
            </DialogFooter>
            </>
          ) : (
            <>
            <DialogHeader className="shrink-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <DialogTitle>Create Program from Scratch</DialogTitle>
                  <DialogDescription>Build a fresh program and assign it directly to {clientName || "this client"}.</DialogDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setStep("choice")} className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              </div>
            </DialogHeader>

            <DialogBody>
              <ProgramEditorFields value={scratchForm} onChange={setScratchForm} />
            </DialogBody>

            <DialogFooter className="shrink-0 border-t pt-4 mt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateFromScratch} disabled={submitting}>
                {submitting ? "Creating..." : hasAssignedProgram ? "Assign New Program" : "Create and Assign"}
              </Button>
            </DialogFooter>
            </>
          )}
        </DialogScrollContent>
      </Dialog>
      <ProgramGenerationChatModal
        open={aiChatOpen}
        onOpenChange={setAiChatOpen}
        title={`Create Program with AI for ${clientName || "this client"}`}
        conversation={aiConversation}
        input={aiChatInput}
        onInputChange={setAiChatInput}
        busy={aiChatBusy}
        onSend={handleSendProgramCreationMessage}
        onRestart={handleRestartProgramCreationConversation}
        onConfirm={handleConfirmProgramCreation}
        canConfirm={Boolean(aiConversation?.conversation_id && latestProgramPreview && !latestProgramParseError)}
        confirmLabel={hasAssignedProgram ? "Assign New Program" : "Create and Assign"}
        programPreview={latestProgramPreview}
        parseError={latestProgramParseError}
      />
    </>
  );
}
